import { useId } from 'react';
import type { FC, ReactNode } from 'react';
import { Button, Text } from '@/shared/ui/primitives';
import { AppModal } from './AppModal';
import styles from './DialogBox.module.css';

interface DialogBoxProps {
  isOpen: boolean;
  message: string | ReactNode;
  confirmText?: string;
  cancelText?: string;
  isDestructive?: boolean;
  confirmTestId?: string;
  cancelTestId?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const DialogBox: FC<DialogBoxProps> = ({
  isOpen,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isDestructive = false,
  confirmTestId,
  cancelTestId,
  onConfirm,
  onCancel,
  isLoading = false,
}) => {
  const descriptionId = useId();
  return (
    <AppModal
      variant="dialog"
      dismissible={!isLoading}
      aria-label={confirmText}
      aria-describedby={descriptionId}
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isLoading) {
          onCancel();
        }
      }}
    >
      <div className={styles.content}>
        <div id={descriptionId}>
          {typeof message === 'string' ? (
            <Text className={styles.message}>{message}</Text>
          ) : (
            message
          )}
        </div>
        <div className={styles.buttons}>
          <Button
            size="lg"
            fullWidth
            variant={isDestructive ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={isLoading}
            data-testid={confirmTestId}
          >
            {isLoading ? 'Loading...' : confirmText}
          </Button>
          <Button
            size="lg"
            fullWidth
            variant="ghost"
            data-modal-initial-focus
            onClick={onCancel}
            disabled={isLoading}
            data-testid={cancelTestId}
          >
            {cancelText}
          </Button>
        </div>
      </div>
    </AppModal>
  );
};
